import * as THREE from 'three/webgpu';
import { isFeatureSurface } from '../../mesh/feature';
import { projectPointOnFeatureSurface } from '../shared/projectPointOnFeatureSurface';

/**
 * 读取数值。
 *
 * @param input 输入值。
 * @returns 有效数值；非法时返回 null。
 */
export function readNumber(input: unknown): number | null {
  if (!Number.isFinite(input)) {
    return null;
  }
  return Number(input);
}

/**
 * 归一化三维向量。
 *
 * @param value 输入向量。
 * @returns 单位向量；退化时返回 null。
 */
export function normalizeVec3(value: THREE.Vector3): THREE.Vector3 | null {
  const length = value.length();
  if (!Number.isFinite(length) || length < 1e-12) {
    return null;
  }
  return value.clone().normalize();
}

/**
 * 数值钳制。
 *
 * @param value 输入值。
 * @param min 最小值。
 * @param max 最大值。
 * @returns 钳制后数值。
 */
export function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * 构造稳定垂直向量。
 *
 * @param unit 已归一化向量。
 * @returns 垂向单位向量；不可构造时返回 null。
 */
export function buildAnyPerpendicularUnitVec3(unit: THREE.Vector3): THREE.Vector3 | null {
  const seed = Math.abs(unit.x) < 0.9
    ? new THREE.Vector3(1, 0, 0)
    : (Math.abs(unit.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1));
  return normalizeVec3(seed.clone().cross(unit));
}

/**
 * 判断数值是否位于闭区间内。
 *
 * @param value 待判断值。
 * @param min 最小值。
 * @param max 最大值。
 * @param epsilon 容差。
 * @returns 是否在区间内。
 */
export function isWithinInclusiveRange(value: number, min: number, max: number, epsilon: number): boolean {
  return value >= min - epsilon && value <= max + epsilon;
}

/**
 * 判断二维点是否在线段上。
 *
 * @param point 待判断点。
 * @param start 线段起点。
 * @param end 线段终点。
 * @param epsilon 容差。
 * @returns 是否在线段上。
 */
export function isPointOnSegment2D(point: THREE.Vector3, start: THREE.Vector3, end: THREE.Vector3, epsilon: number): boolean {
  const segmentDx = end.x - start.x;
  const segmentDy = end.y - start.y;
  const pointDx = point.x - start.x;
  const pointDy = point.y - start.y;
  const cross = segmentDx * pointDy - segmentDy * pointDx;
  const scale = Math.max(1, Math.hypot(segmentDx, segmentDy));
  if (Math.abs(cross) > epsilon * scale) {
    return false;
  }
  return isWithinInclusiveRange(point.x, Math.min(start.x, end.x), Math.max(start.x, end.x), epsilon)
    && isWithinInclusiveRange(point.y, Math.min(start.y, end.y), Math.max(start.y, end.y), epsilon);
}

/**
 * 判断候选交点是否同时落在线段 A 和线段 B 上。
 *
 * @param point 待判断点。
 * @param aStart 线段 A 起点。
 * @param aEnd 线段 A 终点。
 * @param bStart 线段 B 起点。
 * @param bEnd 线段 B 终点。
 * @returns 是否同时在线段 A/B 上。
 */
export function isPointOnBothSegments(
  point: THREE.Vector3,
  aStart: THREE.Vector3,
  aEnd: THREE.Vector3,
  bStart: THREE.Vector3,
  bEnd: THREE.Vector3,
): boolean {
  const epsilon = 1e-9;
  return isPointOnSegment2D(point, aStart, aEnd, epsilon) && isPointOnSegment2D(point, bStart, bEnd, epsilon);
}

/**
 * 计算二维平面内两条线段的交点。
 *
 * @param aStart 线段 A 起点。
 * @param aEnd 线段 A 终点。
 * @param bStart 线段 B 起点。
 * @param bEnd 线段 B 终点。
 * @returns 交点；不存在时返回 null。
 */
export function computePlanarSegmentIntersection(
  aStart: THREE.Vector3,
  aEnd: THREE.Vector3,
  bStart: THREE.Vector3,
  bEnd: THREE.Vector3,
): THREE.Vector3 | null {
  const epsilon = 1e-9;
  const x1 = aStart.x; const y1 = aStart.y;
  const x2 = aEnd.x; const y2 = aEnd.y;
  const x3 = bStart.x; const y3 = bStart.y;
  const x4 = bEnd.x; const y4 = bEnd.y;
  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denominator) < epsilon) {
    const sharedPoints = [aStart, aEnd].filter((pointA) => (
      [bStart, bEnd].some((pointB) => (
        Math.abs(pointA.x - pointB.x) < epsilon
        && Math.abs(pointA.y - pointB.y) < epsilon
        && Math.abs(pointA.z - pointB.z) < epsilon
      ))
    ));
    return sharedPoints[0] ?? null;
  }
  const tNumerator = (x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4);
  const uNumerator = (x1 - x3) * (y1 - y2) - (y1 - y3) * (x1 - x2);
  const t = tNumerator / denominator;
  const u = uNumerator / denominator;
  if (!isWithinInclusiveRange(t, 0, 1, epsilon) || !isWithinInclusiveRange(u, 0, 1, epsilon)) {
    return null;
  }
  const px = x1 + (x2 - x1) * t;
  const py = y1 + (y2 - y1) * t;
  const az = aStart.z + (aEnd.z - aStart.z) * t;
  const bz = bStart.z + (bEnd.z - bStart.z) * u;
  return new THREE.Vector3(px, py, (az + bz) / 2);
}

/**
 * 获取线段端点。
 *
 * @param entity 目标实体。
 * @returns 线段端点；缺失时返回 null。
 */
export function tryGetLineEndpoints(entity: {
  data: {
    /** 线段起点。 */
    startPoint?: THREE.Vector3 | null;
    /** 线段终点。 */
    endPoint?: THREE.Vector3 | null;
  };
}): LineSegmentEndpoints | null {
  const start = entity.data.startPoint?.clone() ?? null;
  const end = entity.data.endPoint?.clone() ?? null;
  if (!start || !end) {
    return null;
  }
  return { start, end };
}

/**
 * 点投影到平面。
 *
 * @param point 源点。
 * @param planePoint 平面上一点。
 * @param planeNormal 平面法向。
 * @returns 投影点；失败时返回 null。
 */
export function projectPointToPlane(point: THREE.Vector3, planePoint: THREE.Vector3, planeNormal: THREE.Vector3): THREE.Vector3 | null {
  const unitNormal = normalizeVec3(planeNormal);
  if (!unitNormal) {
    return null;
  }
  const offset = point.clone().sub(planePoint);
  const distance = offset.dot(unitNormal);
  return point.clone().addScaledVector(unitNormal, -distance);
}

/**
 * 投影点到受支持曲面对象。
 *
 * @param surfaceObject 曲面对象。
 * @param point 源点。
 * @returns 投影点；不可投影时返回 null。
 */
export function projectPointOnSupportedSurface(surfaceObject: unknown, point: THREE.Vector3): THREE.Vector3 | null {
  if (!isFeatureSurface(surfaceObject) || surfaceObject.surfaceKind === 'plane') {
    return null;
  }
  const candidate = projectPointOnFeatureSurface(surfaceObject, point);
  return candidate?.point ?? null;
}
